/* ==========================================================================
   Ulixo theme — global behaviour
   ========================================================================== */

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` }
  };
}


/* ---------- Reliable horizontal scrolling ----------
   CSS `scroll-behavior: smooth` silently cancels programmatic scrollTo/scrollBy
   on these snap containers, so we drive scrollLeft ourselves and momentarily
   suspend snapping so it can't fight the animation mid-flight.            */
function scrollElTo(el, left, smooth = true, onDone) {
  if (!el) return;
  const max = Math.max(0, el.scrollWidth - el.clientWidth);
  const target = Math.max(0, Math.min(Math.round(left), max));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!smooth || reduceMotion) {
    el.scrollLeft = target;
    if (onDone) onDone();
    return;
  }

  const from = el.scrollLeft;
  const delta = target - from;
  if (Math.abs(delta) < 2) {
    el.scrollLeft = target;
    if (onDone) onDone();
    return;
  }

  const snap = el.style.scrollSnapType;
  el.style.scrollSnapType = 'none';

  const duration = 380;
  const start = performance.now();
  const ease = (t) => 1 - Math.pow(1 - t, 3);
  let done = false;

  const finish = () => {
    if (done) return;
    done = true;
    el.scrollLeft = target;
    el.style.scrollSnapType = snap;
    if (onDone) onDone();
  };

  const tick = (now) => {
    if (done) return;
    const t = Math.min(1, (now - start) / duration);
    el.scrollLeft = from + delta * ease(t);
    if (t < 1) requestAnimationFrame(tick);
    else finish();
  };

  requestAnimationFrame(tick);
  // guarantee arrival even if rAF is throttled (backgrounded tab)
  setTimeout(finish, duration + 240);
}


/* Smooth window scrolling, driven manually for the same reason as scrollElTo. */
function ulxScrollWindowTo(y) {
  const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  const target = Math.max(0, Math.min(Math.round(y), max));
  const from = window.scrollY;
  const delta = target - from;
  if (Math.abs(delta) < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    window.scrollTo(0, target);
    return;
  }
  const duration = 420;
  const start = performance.now();
  const ease = (t) => 1 - Math.pow(1 - t, 3);
  let done = false;
  const finish = () => { if (done) return; done = true; window.scrollTo(0, target); };
  const tick = (now) => {
    if (done) return;
    const t = Math.min(1, (now - start) / duration);
    window.scrollTo(0, from + delta * ease(t));
    if (t < 1) requestAnimationFrame(tick);
    else finish();
  };
  requestAnimationFrame(tick);
  setTimeout(finish, duration + 260);
}
window.ulxScrollWindowTo = ulxScrollWindowTo;

/* ---------- Sticky group manager: exposes header height as a CSS var ---------- */
class StickyGroupManager extends HTMLElement {
  connectedCallback() {
    this.setHeights();
    window.addEventListener('resize', debounce(this.setHeights.bind(this), 100));
  }

  setHeights() {
    const header = this.querySelector('.section-header');
    const height = header ? header.offsetHeight : 0;
    document.documentElement.style.setProperty('--header-height', `${height}px`);
  }
}
customElements.define('sticky-group-manager', StickyGroupManager);

/* ---------- Sticky header: hides on scroll down, reveals on scroll up ---------- */
class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.headerBounds = {};
    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.predictiveSearch = this.querySelector('predictive-search');
    this.onScrollHandler = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScrollHandler, false);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    if (this.dataset.stickyType !== 'on-scroll-up') return;

    if (scrollTop > this.currentScrollTop && scrollTop > 200) {
      this.classList.add('scrolled-past-header');
      if (this.preventHide) return;
      requestAnimationFrame(this.hide.bind(this));
    } else if (scrollTop < this.currentScrollTop && scrollTop > 200) {
      if (!this.preventReveal) requestAnimationFrame(this.reveal.bind(this));
    } else if (scrollTop <= 200) {
      requestAnimationFrame(this.reset.bind(this));
    }
    this.currentScrollTop = scrollTop;
  }

  hide() { this.style.transform = 'translateY(-100%)'; }
  reveal() { this.style.transform = 'translateY(0)'; }
  reset() { this.style.transform = 'translateY(0)'; }
}
customElements.define('sticky-header', StickyHeader);

/* ---------- Menu drawer ---------- */
class HeaderDrawer extends HTMLElement {
  constructor() {
    super();
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.addEventListener('click', (event) => {
      if (event.target.closest('.menu-drawer__close-menu-btn')) {
        event.preventDefault();
        this.close();
      }
    });
    document.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });
  }

  close() {
    if (!this.details) return;
    this.details.removeAttribute('open');
    if (this.summary) this.summary.setAttribute('aria-expanded', 'false');
  }
}
customElements.define('header-drawer', HeaderDrawer);

/* ---------- Details modal (search) ---------- */
class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');

    this.detailsContainer.addEventListener('keyup', (e) => e.code === 'Escape' && this.close());
    if (this.summaryToggle) {
      this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
      this.summaryToggle.setAttribute('role', 'button');
    }
    const closeButton = this.querySelector('button[type="button"].modal__close-button');
    if (closeButton) closeButton.addEventListener('click', this.close.bind(this));
    const overlay = this.querySelector('.modal-overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
  }

  onSummaryClick(event) {
    event.currentTarget.setAttribute('aria-expanded', !this.detailsContainer.hasAttribute('open'));
    if (!this.detailsContainer.hasAttribute('open')) {
      setTimeout(() => {
        const input = this.querySelector('input[type="search"]');
        if (input) input.focus();
      }, 50);
    }
  }

  close() {
    this.detailsContainer.removeAttribute('open');
    if (this.summaryToggle) this.summaryToggle.setAttribute('aria-expanded', false);
  }
}
customElements.define('details-modal', DetailsModal);

/* ---------- Cart drawer ---------- */
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (e) => e.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
  }

  open() {
    this.classList.add('active');
    document.body.classList.add('overflow-hidden');
    const inner = this.querySelector('.drawer__inner');
    if (inner) setTimeout(() => inner.focus(), 100);
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
  }

  async refresh() {
    try {
      const response = await fetch(`${window.shopUrl}${window.routes.cart_url}?section_id=cart-drawer`);
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const newDrawer = html.querySelector('cart-drawer');
      if (newDrawer) this.innerHTML = newDrawer.innerHTML;
      this.updateBubble(html);
    } catch (e) {
      console.error('[cart-drawer] refresh failed', e);
    }
  }

  updateBubble(html) {
    const source = html ? html.querySelector('#cart-icon-bubble') : null;
    const target = document.querySelector('#cart-icon-bubble');
    if (source && target) target.innerHTML = source.innerHTML;
  }
}
customElements.define('cart-drawer', CartDrawer);

async function refreshCartUI() {
  const drawer = document.querySelector('cart-drawer');
  if (drawer) {
    await drawer.refresh();
    return;
  }
  const response = await fetch(`${window.shopUrl}${window.routes.cart_url}?section_id=header`);
  const text = await response.text();
  const html = new DOMParser().parseFromString(text, 'text/html');
  const source = html.querySelector('#cart-icon-bubble');
  const target = document.querySelector('#cart-icon-bubble');
  if (source && target) target.innerHTML = source.innerHTML;
}

/* ---------- Product form (add to cart) ---------- */
class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    this.errorWrapper = this.querySelector('.product-form__error-message-wrapper');
    this.submitButton = this.querySelector('[type="submit"]');
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') return;
    this.handleSubmit(event);
  }

  async handleSubmit() {
    this.setLoading(true);
    this.hideError();

    const formData = new FormData(this.form);
    const body = { items: [], sections_url: window.location.pathname };

    // A bundle form carries several `items[][id]` pairs; a normal form carries one `id`.
    const bundleIds = formData.getAll('bundle-id');
    if (bundleIds.length) {
      // Shopify collapses duplicate variant ids in one /cart/add call into a
      // single line, so two rows of the same size arrived as quantity 1.
      // Aggregate first and send a real quantity per variant.
      const counts = new Map();
      bundleIds.forEach((raw) => {
        const id = Number(raw);
        if (!id) return;
        counts.set(id, (counts.get(id) || 0) + 1);
      });
      counts.forEach((quantity, id) => body.items.push({ id, quantity }));
    } else {
      body.items.push({
        id: Number(formData.get('id')),
        quantity: Number(formData.get('quantity') || 1)
      });
    }

    try {
      const response = await fetch(`${window.routes.cart_add_url}.js`, {
        ...fetchConfig('javascript'),
        body: JSON.stringify(body)
      });
      const data = await response.json();

      if (data.status) {
        this.showError(data.description || data.message);
        return;
      }

      await refreshCartUI();

      const discountCode = this.dataset.discountCode;
      if (discountCode) {
        window.location.href = `/discount/${encodeURIComponent(discountCode)}?redirect=${encodeURIComponent(window.routes.cart_url)}`;
        return;
      }

      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.open();
      else window.location.href = window.routes.cart_url;
    } catch (e) {
      console.error('[product-form]', e);
      this.showError(window.cartStrings.error);
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(state) {
    if (!this.submitButton) return;
    this.submitButton.classList.toggle('loading', state);
    this.submitButton.setAttribute('aria-disabled', state);
    const spinner = this.submitButton.querySelector('.loading-overlay__spinner');
    if (spinner) spinner.classList.toggle('hidden', !state);
  }

  showError(message) {
    if (!this.errorWrapper) return;
    this.errorWrapper.hidden = false;
    const target = this.errorWrapper.querySelector('.product-form__error-message');
    if (target) target.textContent = message;
  }

  hideError() {
    if (this.errorWrapper) this.errorWrapper.hidden = true;
  }
}
customElements.define('product-form', ProductForm);

/* ---------- Variant selection ---------- */
class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange.bind(this));
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.updateLabels();
    this.toggleAddButton(!this.currentVariant);
    this.updateAvailability();

    if (!this.currentVariant) {
      this.updatePrice(null);
      return;
    }
    this.updateURL();
    this.updateVariantInput();
    this.updateMedia();
    this.renderProductInfo();
  }

  get variantData() {
    if (!this._variantData) {
      const script = this.querySelector('[type="application/json"]');
      this._variantData = script ? JSON.parse(script.textContent) : [];
    }
    return this._variantData;
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('input[type="radio"]:checked'), (input) => input.value);
  }

  updateMasterId() {
    this.currentVariant = this.variantData.find((variant) =>
      this.options.every((option, index) => option === variant[`option${index + 1}`])
    );
  }

  updateLabels() {
    this.querySelectorAll('[id^="custom-label-"]').forEach((label, index) => {
      const base = label.dataset.baseLabel || label.textContent.split(' - ')[0].trim();
      label.dataset.baseLabel = base;
      if (this.options && this.options[index]) label.textContent = `${base} - ${this.options[index]}`;
    });
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    document
      .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
      .forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (!input) return;
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
  }

  updateAvailability() {
    const availableOptionValues = new Set();
    this.variantData.forEach((variant) => {
      if (variant.available) {
        availableOptionValues.add(`1:${variant.option1}`);
        if (this.options && this.options[0] === variant.option1) availableOptionValues.add(`2:${variant.option2}`);
        if (this.options && this.options[0] === variant.option1 && this.options[1] === variant.option2) {
          availableOptionValues.add(`3:${variant.option3}`);
        }
      }
    });

    this.querySelectorAll('input[type="radio"]').forEach((input) => {
      const wrapper = input.closest('.product-form__input') || input.closest('.color-swatch');
      const group = input.closest('.product-form__input');
      const index = Array.from(this.querySelectorAll('.product-form__input')).indexOf(group) + 1;
      if (index <= 1) return; // first option is always selectable
      input.disabled = !availableOptionValues.has(`${index}:${input.value}`);
      if (wrapper) wrapper.classList.toggle('is-unavailable', input.disabled);
    });
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant.featured_media) return;
    const gallery = document.querySelector(`media-gallery[data-section="${this.dataset.section}"]`);
    if (gallery) gallery.setActiveMedia(this.currentVariant.featured_media.id);
  }

  toggleAddButton(disable = true) {
    const form = document.getElementById(`product-form-${this.dataset.section}`);
    if (!form) return;
    const button = form.querySelector('[name="add"]');
    if (!button) return;
    const text = button.querySelector('.main-atc__label__text') || button.querySelector('.button__label');
    if (disable) {
      button.setAttribute('disabled', 'disabled');
      if (text) text.textContent = this.currentVariant === undefined ? window.variantStrings.unavailable : window.variantStrings.soldOut;
    } else {
      button.removeAttribute('disabled');
      if (text) text.textContent = window.variantStrings.addToCart;
    }
  }

  async renderProductInfo() {
    try {
      const response = await fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`);
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      this.swap(html, `price-${this.dataset.section}`);
      this.swap(html, `Sticky-Atc-Price-${this.dataset.section}`);

      const soldOut = !this.currentVariant.available;
      this.toggleAddButton(soldOut);
      const button = document.querySelector(`#product-form-${this.dataset.section} [name="add"]`);
      if (button && !soldOut) button.removeAttribute('disabled');
    } catch (e) {
      console.error('[variant-selects] render failed', e);
    }
  }

  swap(html, id) {
    const source = html.getElementById(id);
    const target = document.getElementById(id);
    if (source && target) target.innerHTML = source.innerHTML;
  }
}
customElements.define('variant-selects', VariantSelects);

/* ---------- Media gallery ---------- */
class MediaGallery extends HTMLElement {
  constructor() {
    super();
    this.viewer = this.querySelector('[id^="Slider-Gallery"]');
    this.thumbnails = this.querySelector('[id^="Slider-Thumbnails"]');
    if (this.thumbnails) {
      this.thumbnails.querySelectorAll('[data-target]').forEach((button) => {
        button.addEventListener('click', () => this.setActiveMedia(button.dataset.target, true));
      });
    }
    this.bindArrows();
  }

  bindArrows() {
    const prev = this.querySelector('.slider-button--prev');
    const next = this.querySelector('.slider-button--next');
    if (!this.viewer) return;
    if (prev) prev.addEventListener('click', () => this.scrollBySlide(-1));
    if (next) next.addEventListener('click', () => this.scrollBySlide(1));

    // thumbnail rail chevrons page the strip left/right
    const tPrev = this.querySelector('.thumbnail-nav__arrow--prev');
    const tNext = this.querySelector('.thumbnail-nav__arrow--next');
    const page = (dir) => {
      if (!this.thumbnails) return;
      scrollElTo(this.thumbnails, this.thumbnails.scrollLeft + this.thumbnails.clientWidth * 0.8 * dir, true, () => this.syncThumbArrows());
    };
    if (tPrev) tPrev.addEventListener('click', () => page(-1));
    if (tNext) tNext.addEventListener('click', () => page(1));

    // `scroll` is unreliable on these snap containers, so settle on every
    // signal that does fire, and explicitly after each programmatic scroll.
    // NOTE: the thumbnail rail gets arrow-state only. Running the full settle
    // there made its own scroll snap it back to the active thumb, so dragging
    // to the end and back fought the user.
    const settleStage = debounce(() => this.settle(), 90);
    const settleRail = debounce(() => this.syncThumbArrows(), 90);
    ['scroll', 'scrollend', 'pointerup', 'touchend', 'mouseup'].forEach((evt) => {
      this.viewer.addEventListener(evt, settleStage, { passive: true });
      if (this.thumbnails) this.thumbnails.addEventListener(evt, settleRail, { passive: true });
    });

    // while the rail is being dragged, never auto-scroll it
    if (this.thumbnails) {
      const hold = () => {
        this.railBusy = true;
        clearTimeout(this.railTimer);
      };
      const release = () => {
        clearTimeout(this.railTimer);
        this.railTimer = setTimeout(() => { this.railBusy = false; }, 700);
      };
      ['pointerdown', 'touchstart', 'mousedown', 'wheel'].forEach((e) =>
        this.thumbnails.addEventListener(e, hold, { passive: true })
      );
      ['pointerup', 'touchend', 'mouseup', 'pointercancel', 'touchcancel'].forEach((e) =>
        this.thumbnails.addEventListener(e, release, { passive: true })
      );
      this.thumbnails.addEventListener('scroll', release, { passive: true });
    }

    // arrow state depends on scrollWidth, which is meaningless until the
    // images have actually laid out — so re-sync at several settle points
    const sync = () => { this.syncArrows(); this.syncThumbArrows(); };
    window.addEventListener('resize', debounce(sync, 150));
    requestAnimationFrame(sync);
    window.addEventListener('load', sync);
    setTimeout(sync, 300);
    setTimeout(sync, 1200);
    this.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', debounce(sync, 60), { once: true });
    });
    this.querySelectorAll('.slider-counter__link--dots').forEach((dot, index) => {
      dot.addEventListener('click', () => {
        const slides = this.viewer.querySelectorAll('.slider__slide');
        if (slides[index]) scrollElTo(this.viewer, slides[index].offsetLeft - this.viewer.offsetLeft, true, () => this.settle());
      });
    });
  }

  settle() {
    this.syncArrows();
    this.syncThumbArrows();
    this.syncActiveFromScroll();
  }

  scrollBySlide(direction) {
    const slide = this.viewer.querySelector('.slider__slide');
    if (!slide) return;
    scrollElTo(this.viewer, this.viewer.scrollLeft + slide.offsetWidth * direction, true, () => this.settle());
  }

  syncArrows() {
    if (!this.viewer) return;
    const max = this.viewer.scrollWidth - this.viewer.clientWidth - 2;
    const prev = this.querySelector('.slider-button--prev');
    const next = this.querySelector('.slider-button--next');
    if (prev) prev.disabled = this.viewer.scrollLeft <= 2;
    if (next) next.disabled = this.viewer.scrollLeft >= max;
  }

  syncThumbArrows() {
    if (!this.thumbnails) return;
    const max = this.thumbnails.scrollWidth - this.thumbnails.clientWidth - 2;
    const prev = this.querySelector('.thumbnail-nav__arrow--prev');
    const next = this.querySelector('.thumbnail-nav__arrow--next');
    if (prev) prev.disabled = this.thumbnails.scrollLeft <= 2;
    if (next) next.disabled = this.thumbnails.scrollLeft >= max;
  }

  syncDots() { this.settle(); }

  syncActiveFromScroll() {
    const slides = Array.from(this.viewer.querySelectorAll('.slider__slide'));
    if (!slides.length) return;
    const index = Math.round(this.viewer.scrollLeft / slides[0].offsetWidth);
    this.querySelectorAll('.slider-counter__link--dots').forEach((dot, i) => {
      dot.classList.toggle('slider-counter__link--active', i === index);
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
    const thumbs = this.querySelectorAll('.thumbnail');
    thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', i === index ? 'true' : 'false'));

    // only follow the rail when the slide genuinely changed, and never while
    // the visitor is scrolling the rail themselves
    if (index !== this.activeIndex) {
      this.activeIndex = index;
      if (thumbs[index] && !this.railBusy) this.setActiveThumbView(thumbs[index]);
    }
  }

  setActiveThumbView(thumb) {
    if (!this.thumbnails) return;
    const item = thumb.closest('.thumbnail-slider__item') || thumb;
    const left = item.offsetLeft - this.thumbnails.offsetLeft;
    const right = left + item.offsetWidth;
    const viewLeft = this.thumbnails.scrollLeft;
    const viewRight = viewLeft + this.thumbnails.clientWidth;
    if (left < viewLeft || right > viewRight) {
      scrollElTo(this.thumbnails, left - this.thumbnails.clientWidth / 2 + item.offsetWidth / 2);
    }
  }

  setActiveMedia(mediaId, focus = false) {
    if (!this.viewer) return;
    const target = this.viewer.querySelector(`[data-media-id$="${mediaId}"]`);
    if (!target) return;

    scrollElTo(this.viewer, target.offsetLeft - this.viewer.offsetLeft, focus, () => this.settle());
    this.viewer.querySelectorAll('.slider__slide').forEach((slide) => slide.classList.remove('is-active'));
    target.classList.add('is-active');

    this.querySelectorAll('.thumbnail').forEach((thumb) => {
      const active = thumb.dataset.target === String(mediaId);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
      // keep the active thumbnail inside the carousel viewport
      if (active && this.thumbnails && !this.railBusy) {
        const item = thumb.closest('.thumbnail-slider__item') || thumb;
        const left = item.offsetLeft - this.thumbnails.offsetLeft;
        const right = left + item.offsetWidth;
        const viewLeft = this.thumbnails.scrollLeft;
        const viewRight = viewLeft + this.thumbnails.clientWidth;
        if (left < viewLeft || right > viewRight) {
          scrollElTo(this.thumbnails, left - this.thumbnails.clientWidth / 2 + item.offsetWidth / 2);
        }
      }
    });
  }
}
customElements.define('media-gallery', MediaGallery);

/* ---------- Generic slider wrapper (product media) ---------- */
class SliderComponent extends HTMLElement {}
customElements.define('slider-component', SliderComponent);

/* ---------- Image slider carousel ---------- */
class UlxSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.ulx-slider__track');
    this.slides = Array.from(this.querySelectorAll('.ulx-slider__slide'));
    if (!this.track || !this.slides.length) return;

    this.pages = Array.from(this.querySelectorAll('.ulx-slider__page'));
    this.querySelector('.ulx-slider__arrow--prev')?.addEventListener('click', () => this.move(-1));
    this.querySelector('.ulx-slider__arrow--next')?.addEventListener('click', () => this.move(1));
    this.pages.forEach((page, index) => page.addEventListener('click', () => this.goTo(index)));
    this.track.addEventListener('scroll', debounce(() => this.sync(), 80));
    this.initDrag();

    if (this.dataset.autoplay === 'true') {
      const speed = (parseFloat(this.dataset.autoplaySpeed) || 5) * 1000;
      this.timer = setInterval(() => this.move(1, true), speed);
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
    }
  }

  /* Touch already swipes natively via overflow-x + scroll-snap.
     This adds click-and-drag on desktop, where there is no touch gesture. */
  initDrag() {
    const track = this.track;
    let down = false, moved = false, startX = 0, startScroll = 0;

    const end = () => {
      if (!down) return;
      down = false;
      track.style.cursor = track.scrollWidth > track.clientWidth ? 'grab' : '';
      track.style.scrollSnapType = '';
      this.goTo(this.index);
      this.sync();
    };

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      down = true;
      moved = false;
      startX = event.clientX;
      startScroll = track.scrollLeft;
      track.style.scrollSnapType = 'none';
      track.style.cursor = 'grabbing';
    });

    window.addEventListener('pointermove', (event) => {
      if (!down) return;
      const dx = event.clientX - startX;
      if (Math.abs(dx) > 3) moved = true;
      track.scrollLeft = startScroll - dx;
      event.preventDefault();
    });

    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);

    /* a drag that ends on a link or button must not also fire a click */
    track.addEventListener('click', (event) => {
      if (!moved) return;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    track.addEventListener('dragstart', (event) => event.preventDefault());

    if (track.scrollWidth > track.clientWidth) track.style.cursor = 'grab';
  }

  disconnectedCallback() { clearInterval(this.timer); }

  get index() {
    const width = this.slides[0].offsetWidth + this.gap;
    return Math.round(this.track.scrollLeft / width);
  }

  get gap() {
    return parseFloat(getComputedStyle(this.querySelector('.ulx-slider__list')).columnGap) || 0;
  }

  move(direction, loop = false) {
    let next = this.index + direction;
    if (next >= this.slides.length) next = loop ? 0 : this.slides.length - 1;
    if (next < 0) next = loop ? this.slides.length - 1 : 0;
    this.goTo(next);
  }

  goTo(index) {
    const slide = this.slides[index];
    if (!slide) return;
    scrollElTo(this.track, slide.offsetLeft - this.track.offsetLeft);
  }

  sync() {
    const current = this.index;
    this.pages.forEach((page, i) => page.classList.toggle('is-active', i === current));
  }
}
customElements.define('ulx-slider', UlxSlider);

/* ---------- Autoplaying inline video ---------- */
class InternalVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    this.video.muted = true;
    this.video.playsInline = true;

    const button = this.querySelector('.internal-video__play');
    if (button) {
      button.addEventListener('click', () => {
        if (this.video.paused) this.play();
        else this.pause();
      });
    }

    if (this.dataset.autoplay === 'true') {
      this.observer = new IntersectionObserver(
        (entries) => entries.forEach((entry) => (entry.isIntersecting ? this.play() : this.pause())),
        { threshold: 0.25 }
      );
      this.observer.observe(this);
    }
  }

  disconnectedCallback() { this.observer?.disconnect(); }

  play() {
    const source = this.video.querySelector('source[data-src]');
    if (source && !source.src) {
      source.src = source.dataset.src;
      this.video.load();
    }
    const promise = this.video.play();
    if (promise) promise.then(() => this.classList.add('internal-video--playing')).catch(() => {});
  }

  pause() {
    this.video.pause();
    this.classList.remove('internal-video--playing');
  }
}
customElements.define('internal-video', InternalVideo);

/* ---------- Sticky add-to-cart bar ---------- */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.anchor = document.querySelector('.main-product-atc') || document.querySelector('product-form[data-main="true"]');
    this.scrollButton = this.querySelector('.sticky-atc__scroll-btn');

    if (this.scrollButton) {
      this.scrollButton.addEventListener('click', () => {
        const destination = document.querySelector(this.dataset.scrollDestination || '.main-product-form');
        if (destination) {
          const rect = destination.getBoundingClientRect();
          ulxScrollWindowTo(window.scrollY + rect.top - (window.innerHeight - rect.height) / 2);
        }
      });
    }

    if (!this.anchor) { this.show(); return; }

    this.observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (this.dataset.afterScroll === 'true') {
          entry.isIntersecting ? this.hide() : (entry.boundingClientRect.top < 0 ? this.show() : this.hide());
        } else {
          entry.isIntersecting ? this.hide() : this.show();
        }
      }),
      { threshold: 0 }
    );
    this.observer.observe(this.anchor);
  }

  disconnectedCallback() { this.observer?.disconnect(); }

  show() {
    this.classList.add('is-visible');
    document.documentElement.style.setProperty('--sticky-atc-offset', `${this.offsetHeight}px`);
  }

  hide() {
    this.classList.remove('is-visible');
    document.documentElement.style.setProperty('--sticky-atc-offset', '0px');
  }
}
customElements.define('sticky-atc', StickyAtc);

/* ---------- Bundle deals ---------- */
class BundleDeals extends HTMLElement {
  connectedCallback() {
    this.rows = Array.from(this.querySelectorAll('.bundle-deals__product-js'));
    this.currencySymbol = this.dataset.currencySymbol || '$';
    this.percentageLeft = parseFloat(this.dataset.percentageLeft || '1');
    this.fixedDiscount = parseFloat(this.dataset.fixedDiscount || '0');

    this.rows.forEach((row) => {
      row.querySelectorAll('.variant-dropdown').forEach((select) => {
        select.addEventListener('change', () => this.onRowChange(row));
      });
    });

    this.updateTotals();
  }

  variantsFor(row) {
    const script = row.querySelector('[type="application/json"]');
    return script ? JSON.parse(script.textContent) : [];
  }

  onRowChange(row) {
    const selected = Array.from(row.querySelectorAll('.variant-dropdown'), (s) => s.value);
    const variant = this.variantsFor(row).find((v) =>
      selected.every((value, index) => value === v[`option${index + 1}`])
    );

    const input = row.querySelector('.bundle-deals__checkbox-js');
    if (variant && input) {
      input.dataset.id = variant.id;
      input.dataset.price = variant.price;
      input.dataset.comparePrice = variant.compare_at_price || variant.price;
      input.checked = variant.available;

      const image = row.querySelector('.bundle-deals__media-item-img-js');
      if (image && variant.featured_image) image.src = `${variant.featured_image.src}&width=500`;

      const price = row.querySelector('.bundle-deals__price-js');
      if (price) price.textContent = this.money(variant.price * this.percentageLeft);
      const compare = row.querySelector('.bundle-deals__compare-price-js');
      if (compare) compare.textContent = this.money(variant.compare_at_price || variant.price);
    }

    this.updateTotals();
  }

  updateTotals() {
    let total = 0;
    let compareTotal = 0;

    this.querySelectorAll('.bundle-deals__checkbox-js').forEach((input) => {
      if (!input.checked) return;
      total += parseFloat(input.dataset.price || '0') * this.percentageLeft;
      compareTotal += parseFloat(input.dataset.comparePrice || input.dataset.price || '0');
    });

    total = Math.max(0, total - this.fixedDiscount * 100);

    const totalEl = this.querySelector('.bundle-deals__total-price-js');
    if (totalEl) totalEl.textContent = this.money(total);
    const compareEl = this.querySelector('.bundle-deals__total-compare-price-js');
    if (compareEl) compareEl.textContent = this.money(compareTotal);

    // Feed the sibling add-to-cart form.
    const form = this.closest('.bundle-offer')?.querySelector('.product-form__variants');
    if (form) {
      form.innerHTML = '';
      this.querySelectorAll('.bundle-deals__checkbox-js').forEach((input) => {
        if (!input.checked) return;
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'bundle-id';
        hidden.value = input.dataset.id;
        form.appendChild(hidden);
      });
    }
  }

  money(cents) {
    return `${this.currencySymbol}${(cents / 100).toFixed(2)}`;
  }
}
customElements.define('bundle-deals', BundleDeals);

/* ---------- Cart drawer upsell toggle ---------- */
class CartDrawerUpsell extends HTMLElement {
  connectedCallback() {
    const button = this.querySelector('.upsell-toggle-btn');
    if (!button) return;
    button.addEventListener('click', (event) => {
      event.preventDefault();
      const selected = this.dataset.selected === 'true';
      this.dataset.selected = String(!selected);
      const form = this.querySelector('product-form form');
      if (!selected && form) form.querySelector('button[type="submit"]')?.click();
    });
  }
}
customElements.define('cart-drawer-upsell', CartDrawerUpsell);

/* ---------- Accordions ---------- */
function initAccordions(root = document) {
  root.querySelectorAll('.accordion .accordion__summary').forEach((summary) => {
    if (summary.dataset.bound === 'true') return;
    summary.dataset.bound = 'true';
    summary.addEventListener('click', (event) => {
      event.preventDefault();
      const expanded = summary.getAttribute('aria-expanded') === 'true';
      summary.setAttribute('aria-expanded', String(!expanded));
      const wrapper = document.getElementById(summary.getAttribute('aria-controls'));
      if (wrapper) wrapper.dataset.open = String(!expanded);
    });
  });
}

/* ---------- Quantity inputs ---------- */
class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const previous = this.input.value;
        button.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
        if (previous !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
      })
    );
  }
}
customElements.define('quantity-input', QuantityInput);

/* ---------- Cart line item updates ---------- */
class CartItems extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', debounce((event) => {
      const target = event.target;
      if (!target.matches('.quantity__input')) return;
      this.updateQuantity(target.dataset.index, target.value);
    }, 300));

    this.addEventListener('click', (event) => {
      const remove = event.target.closest('.cart-item__remove');
      if (!remove) return;
      event.preventDefault();
      this.updateQuantity(remove.dataset.index, 0);
    });
  }

  async updateQuantity(line, quantity) {
    this.classList.add('loading');
    try {
      const response = await fetch(`${window.routes.cart_change_url}.js`, {
        ...fetchConfig(),
        body: JSON.stringify({ line, quantity: Number(quantity) })
      });
      await response.json();
      await refreshCartUI();
      if (document.querySelector('.template-cart')) window.location.reload();
    } catch (e) {
      console.error('[cart-items]', e);
    } finally {
      this.classList.remove('loading');
    }
  }
}
customElements.define('cart-drawer-items', CartItems);
customElements.define('cart-items', class extends CartItems {});

/* ---------- Scroll-reveal animations ---------- */
function initAnimations(root = document, immediate = false) {
  const elements = root.querySelectorAll('.animate-section');
  if (!elements.length) return;

  if (immediate) {
    elements.forEach((el) => el.classList.add('animate--shown'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate--shown');
        observer.unobserve(entry.target);
      }
    }),
    { rootMargin: '0px 0px -50px 0px' }
  );
  elements.forEach((el) => observer.observe(el));
}

/* ---------- Ticker: duplicate content so the loop is seamless ---------- */
function initTickers(root = document) {
  root.querySelectorAll('.horizontal-ticker').forEach((ticker) => {
    const container = ticker.querySelector('.horizontal-ticker__container');
    if (!container || container.dataset.cloned === 'true') return;
    const original = container.innerHTML;
    let guard = 0;
    while (container.scrollWidth < window.innerWidth * 2 && guard < 8) {
      container.innerHTML += original;
      guard += 1;
    }
    container.innerHTML += container.innerHTML;
    container.dataset.cloned = 'true';
  });
}

/* ---------- Cart icon opens the drawer ---------- */
function initCartTriggers(root = document) {
  root.querySelectorAll('.header__icon--cart').forEach((link) => {
    if (link.dataset.bound === 'true') return;
    link.dataset.bound = 'true';
    link.addEventListener('click', (event) => {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      event.preventDefault();
      drawer.open();
    });
  });
}



/* ---------- Size guide modal ---------- */
class SizeGuideModal extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-size-guide-open]');
    this.overlay = this.querySelector('.size-guide__overlay');
    this.panel = this.querySelector('.size-guide__panel');
    this.body = this.querySelector('[data-size-guide-body]');
    this.image = this.querySelector('[data-size-guide-image]');
    if (!this.trigger || !this.overlay) return;

    this.trigger.addEventListener('click', () => this.open());
    this.querySelectorAll('[data-size-guide-close]').forEach((el) =>
      el.addEventListener('click', () => this.close())
    );
    this.onKey = (e) => {
      if (e.code === 'Escape') this.close();
      if (e.code === 'Tab' && this.isOpen) this.trapFocus(e);
    };

    // tap the chart to view it at full size, tap again to fit
    if (this.image) {
      this.image.style.cursor = 'zoom-in';
      this.image.addEventListener('click', () => {
        const zoomed = this.body.classList.toggle('is-zoomed');
        this.image.style.cursor = zoomed ? 'zoom-out' : 'zoom-in';
        const hint = this.querySelector('[data-size-guide-hint]');
        if (hint) hint.textContent = zoomed ? 'Tap the chart to fit' : 'Tap the chart to zoom';
        if (!zoomed) this.body.scrollLeft = 0;
      });
    }
  }

  open() {
    this.overlay.hidden = false;
    this.isOpen = true;
    document.body.classList.add('size-guide-open');
    document.addEventListener('keyup', this.onKey);
    if (this.panel) this.panel.focus();
  }

  close() {
    this.overlay.hidden = true;
    this.isOpen = false;
    document.body.classList.remove('size-guide-open');
    document.removeEventListener('keyup', this.onKey);
    if (this.body) this.body.classList.remove('is-zoomed');
    if (this.trigger) this.trigger.focus();
  }

  trapFocus(e) {
    const focusables = this.overlay.querySelectorAll('button, [href], img[data-size-guide-image], [tabindex]:not([tabindex="-1"])');
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { first.focus(); }
  }
}
customElements.define('size-guide-modal', SizeGuideModal);

/* ---------- Review wall: batch-render 200 reviews without janking scroll ---------- */
class ReviewWall {
  constructor(root) {
    this.root = root;
    this.grid = root.querySelector('[data-review-grid]');
    this.moreBtn = root.querySelector('[data-review-more]');
    this.progress = root.querySelector('[data-review-progress]');
    const cfgEl = root.querySelector('[data-review-config]');
    if (!this.grid || !cfgEl) return;

    try {
      this.cfg = JSON.parse(cfgEl.textContent);
    } catch (e) {
      console.error('[review-wall] bad config', e);
      return;
    }

    this.reviews = [];
    this.shown = 0;
    this.load();
  }

  async load() {
    try {
      const res = await fetch(this.cfg.src, { cache: 'force-cache' });
      this.reviews = await res.json();
    } catch (e) {
      console.error('[review-wall] could not load reviews', e);
      return;
    }
    this.renderBatch();
    if (this.root.hasAttribute('data-hide-empty') && !this.grid.children.length) {
      const wrap = this.root.closest('.shopify-section') || this.root;
      wrap.hidden = true;
      return;
    }
    if (this.moreBtn) {
      this.moreBtn.addEventListener('click', () => this.renderBatch());
    }
    this.observeTail();
  }

  escape(str) {
    const d = document.createElement('div');
    d.textContent = str == null ? '' : String(str);
    return d.innerHTML;
  }

  formatDate(iso) {
    const parts = String(iso || '').split('-');
    if (parts.length !== 3) return this.escape(iso);
    return `${Number(parts[1])}/${Number(parts[2])}/${parts[0]}`;
  }

  stars(n) {
    let out = '<span class="review-stars review-stars--sm" aria-label="' + n + ' out of 5">';
    for (let i = 1; i <= 5; i += 1) {
      out += `<span class="review-star${i <= n ? ' is-full' : ''}">${ReviewWall.STAR}</span>`;
    }
    return out + '</span>';
  }

  card(r) {
    let text = this.escape(r.t);
    if (r.hl) {
      const hl = this.escape(r.hl);
      text = text.replace(hl, `<mark>${hl}</mark>`);
    }

    const imgUrl = r.img && this.cfg.showImages ? this.cfg.images[r.img] : null;
    const media = imgUrl
      // eager + low priority: CSS multi-column breaks native lazy-loading
      // (position is resolved before columns settle, so it never fetches).
      // Batches only render as you scroll, so this stays cheap either way.
      ? `<div class="review-card__media"><img src="${imgUrl}&width=700" alt="Review photo from ${this.escape(r.n)}" loading="eager" fetchpriority="low" decoding="async" width="700" height="933"></div>`
      : '';

    const verified = r.v
      ? `<span class="review-card__verified">${ReviewWall.CHECK}${this.escape(this.cfg.verifiedLabel)}</span>`
      : '';

    return (
      `<article class="review-card">${media}<div class="review-card__body">` +
      `<div class="review-card__who"><span class="review-card__name">${this.escape(r.n)}</span>${verified}</div>` +
      `<div class="review-card__date">${this.formatDate(r.d)}</div>` +
      this.stars(r.r) +
      `<p class="review-card__text">${text}</p>` +
      `</div></article>`
    );
  }

  renderBatch() {
    const next = this.reviews.slice(this.shown, this.shown + this.cfg.batch);
    if (!next.length) return;

    const frag = document.createElement('div');
    frag.innerHTML = next.map((r) => this.card(r)).join('');
    while (frag.firstChild) this.grid.appendChild(frag.firstChild);

    this.shown += next.length;
    const done = this.shown >= this.reviews.length;
    if (this.moreBtn) this.moreBtn.hidden = done;
    if (this.progress) {
      this.progress.textContent = done
        ? `Showing all ${this.reviews.length} reviews`
        : `Showing ${this.shown} of ${this.reviews.length}`;
    }
  }

  // auto-load the next batch as the reader nears the bottom of the wall
  observeTail() {
    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    // must have real area — a zero-height target yields an empty intersection
    // rect and IntersectionObserver never reports it as intersecting
    sentinel.style.cssText = 'display:block;width:100%;height:1px;';
    this.grid.after(sentinel);
    this.sentinel = sentinel;

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) this.renderBatch(); });
      }, { rootMargin: '800px 0px' }).observe(sentinel);
    }

    const maybeLoad = () => {
      if (this.shown >= this.reviews.length) return true;
      const rect = sentinel.getBoundingClientRect();
      if (rect.top < window.innerHeight + 800) this.renderBatch();
      return this.shown >= this.reviews.length;
    };

    // scroll events are not reliable everywhere, so poll the sentinel too.
    // Cheap (one getBoundingClientRect) and stops as soon as the wall is full.
    const onScroll = debounce(maybeLoad, 120);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    this.poll = setInterval(() => {
      if (maybeLoad()) {
        clearInterval(this.poll);
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      }
    }, 400);
  }
}
ReviewWall.STAR = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" fill="currentColor" aria-hidden="true"><path d="M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3l128.3-68.5 128.3 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329 542.7 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z"/></svg>';
ReviewWall.CHECK = '<svg class="review-verified-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="12" fill="currentColor"/><path d="M6.8 12.3l3.4 3.4 7-7" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

function initReviewWalls(root = document) {
  root.querySelectorAll('.review-wall').forEach((el) => {
    if (el.dataset.bound === 'true') return;
    el.dataset.bound = 'true';
    new ReviewWall(el);
  });
}

/* ---------- Boot ---------- */
function boot(root = document, immediate = false) {
  initAccordions(root);
  initAnimations(root, immediate);
  initTickers(root);
  initCartTriggers(root);
  initReviewWalls(root);
}

document.addEventListener('DOMContentLoaded', () => boot());

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => boot(event.target, true));
  document.addEventListener('shopify:section:reorder', () => boot(document, true));
}
